/**
 * DataLakeMap Palette Variants
 *
 * Dark and light theme palettes for map surfaces. Each palette provides
 * base-map tones (landmass, water, roads, labels, etc.) plus shared
 * fire-overlay tokens (severity ramp, beacon colors).
 *
 * ┌───────────────────────────────────────────────────────────────────┐
 * │ Palette       │ Landmass  │ Vibe                                │
 * ├───────────────┼───────────┼─────────────────────────────────────┤
 * │ deepCanvas    │ #151A28   │ Darkest, max fire glow              │
 * │ dusk          │ #1A2035   │ +1 step lighter, softer navy        │
 * │ twilight      │ #1F2742   │ +2 steps, more blue undertone       │
 * │ slate         │ #263043   │ +3 steps, warm-grey slate           │
 * │ brand         │ #0f172a   │ Matches fb-surface (design tokens)  │
 * │ daylight      │ #f8fafc   │ Light: crisp white                  │
 * │ paper         │ #f1f5f9   │ Light: warm off-white               │
 * │ cloud         │ #e2e8f0   │ Light: soft grey                    │
 * └───────────────────────────────────────────────────────────────────┘
 */

// ── Palette shape ────────────────────────────────────────────────

export type ThemeMode = 'dark' | 'light';

export interface MapPalette {
  /** Theme mode this palette belongs to */
  mode: ThemeMode;
  // Base map
  landmass: string;
  water: string;
  landuse: string;
  minorRoad: string;
  minorRoadOp: number;
  majorRoad: string;
  majorRoadOp: number;
  // Typography
  cityLabel: string;
  minorLabel: string;
  textHalo: string;
  textHaloWidth: number;
  textHaloBlur: number;
  // Topography
  contour: string;
  contourIndexOp: number;
  contourMinorOp: number;
  // Hillshade
  hillshadeShadow: string;
  hillshadeHighlight: string;
  // Buildings
  building: string;
  // Fire overlays (shared across all palettes)
  activeBorder: string;
  activeFill: string;
  activeFillOp: number;
  historicalBorder: string;
  beaconStroke: string;
  beaconStrokeW: number;
  beaconPrimary: string;
  beaconCritical: string;
  severityLow: string;
  severityMod: string;
  severityModerate: string;
  severityHigh: string;
  severityExtreme: string;
  severityFallback: string;
  vintageVeryHigh: string;
  vintageHigh: string;
  vintageModerate: string;
  vintageFallback: string;
}

export type PaletteName = 'deepCanvas' | 'dusk' | 'twilight' | 'slate' | 'brand'
  | 'daylight' | 'paper' | 'cloud';

// ── Shared fire overlay tokens (constant across all palettes) ────

export const FIRE_TOKENS = {
  activeBorder:     '#FBBF24',
  activeFill:       '#FBBF24',
  activeFillOp:     0.13,
  historicalBorder:  '#94A3B8',
  beaconStroke:     '#151A28',
  beaconStrokeW:    0,
  beaconPrimary:    '#FBBF24',
  beaconCritical:   '#EF4444',
  severityLow:      '#FBBF24',
  severityMod:      '#f97316',
  severityModerate: '#eab308',
  severityHigh:     '#ef4444',
  severityExtreme:  '#dc2626',
  severityFallback: '#6b7280',
  vintageVeryHigh:  '#f87171',
  vintageHigh:      '#fb923c',
  vintageModerate:  '#fde047',
  vintageFallback:  '#9ca3af',
} as const;

// ── Helper: build a full palette from base tones ─────────────────

interface PaletteBase {
  mode: ThemeMode;
  landmass: string;
  water: string;
  landuse: string;
  minorRoad: string;
  majorRoad: string;
  cityLabel: string;
  minorLabel: string;
  contour: string;
  hillshadeShadow: string;
  hillshadeHighlight: string;
  building: string;
}

export function makePalette(base: PaletteBase): MapPalette {
  const isDark = base.mode === 'dark';
  return {
    ...base,
    minorRoadOp: isDark ? 0.6 : 0.4,
    majorRoadOp: isDark ? 0.7 : 0.5,
    textHalo: base.landmass,
    textHaloWidth: 1.5,
    textHaloBlur: 1,
    contourIndexOp: isDark ? 0.6 : 0.4,
    contourMinorOp: isDark ? 0.25 : 0.15,
    ...FIRE_TOKENS,
    // Light palettes need a visible beacon stroke ring
    beaconStroke: isDark ? '#151A28' : '#ffffff',
    beaconStrokeW: isDark ? 0 : 1,
  };
}

// ── Dark palettes ─────────────────────────────────────────────────

export const deepCanvas: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#151A28',
  water:      '#0D111A',
  landuse:    '#1E2538',
  minorRoad:  '#252D44',
  majorRoad:  '#3A4663',
  cityLabel:  '#9CA3AF',
  minorLabel: '#838B99',
  contour:    '#2A344D',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#1E2538',
});

export const dusk: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#1A2035',
  water:      '#101624',
  landuse:    '#232B40',
  minorRoad:  '#2D3654',
  majorRoad:  '#45516E',
  cityLabel:  '#A8B0BE',
  minorLabel: '#8B939F',
  contour:    '#323D5A',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#232B40',
});

export const twilight: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#1F2742',
  water:      '#151C30',
  landuse:    '#283248',
  minorRoad:  '#374160',
  majorRoad:  '#515E7A',
  cityLabel:  '#B4BCCA',
  minorLabel: '#8891A0',
  contour:    '#3A4667',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#283248',
});

export const slate: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#263043',
  water:      '#1A2233',
  landuse:    '#2F3A50',
  minorRoad:  '#414D68',
  majorRoad:  '#5D6B86',
  cityLabel:  '#C0C7D3',
  minorLabel: '#969EAE',
  contour:    '#445070',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#2F3A50',
});

export const brand: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#0f172a',
  water:      '#020617',
  landuse:    '#1e293b',
  minorRoad:  '#334155',
  majorRoad:  '#475569',
  cityLabel:  '#cbd5e1',
  minorLabel: '#94a3b8',
  contour:    '#334155',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#1e293b',
});

// ── Light palettes ────────────────────────────────────────────────

export const daylight: MapPalette = makePalette({
  mode:       'light',
  landmass:   '#f8fafc',
  water:      '#bfdbfe',
  landuse:    '#dcfce7',
  minorRoad:  '#cbd5e1',
  majorRoad:  '#94a3b8',
  cityLabel:  '#334155',
  minorLabel: '#64748b',
  contour:    '#94a3b8',
  hillshadeShadow:    '#475569',
  hillshadeHighlight: '#ffffff',
  building:   '#94a3b8',
});

export const paper: MapPalette = makePalette({
  mode:       'light',
  landmass:   '#f1f5f9',
  water:      '#93c5fd',
  landuse:    '#d1fae5',
  minorRoad:  '#cbd5e1',
  majorRoad:  '#9ca3af',
  cityLabel:  '#1e293b',
  minorLabel: '#475569',
  contour:    '#9ca3af',
  hillshadeShadow:    '#64748b',
  hillshadeHighlight: '#ffffff',
  building:   '#9ca3af',
});

export const cloud: MapPalette = makePalette({
  mode:       'light',
  landmass:   '#e2e8f0',
  water:      '#7dd3fc',
  landuse:    '#bbf7d0',
  minorRoad:  '#94a3b8',
  majorRoad:  '#64748b',
  cityLabel:  '#0f172a',
  minorLabel: '#334155',
  contour:    '#64748b',
  hillshadeShadow:    '#475569',
  hillshadeHighlight: '#f8fafc',
  building:   '#64748b',
});

// ── Palette registry ──────────────────────────────────────────────

export const PALETTES: Record<PaletteName, MapPalette> = {
  deepCanvas,
  dusk,
  twilight,
  slate,
  brand,
  daylight,
  paper,
  cloud,
};

/** Get palette names filtered by theme mode. */
export function paletteNamesByMode(mode: ThemeMode): PaletteName[] {
  return (Object.keys(PALETTES) as PaletteName[]).filter(
    (name) => PALETTES[name].mode === mode,
  );
}
